// Employees register. Anchor of EMP201 / EMP501 + payroll runs.
import { useEffect, useState, type FormEvent } from 'react';
import { Plus, Trash2, Save, X, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatMoney } from '../lib/format';

interface Employee {
  id: string; org_id: string; full_name: string;
  id_number: string | null; tax_number: string | null; email: string | null; phone: string | null;
  job_title: string | null; hire_date: string | null; end_date: string | null;
  pay_type: 'monthly' | 'hourly'; base_salary: number;
  uif_applicable: boolean; sdl_applicable: boolean; paye_directive: string | null;
  bank_name: string | null; bank_account_number: string | null; bank_branch_code: string | null;
  notes: string | null; is_archived: boolean;
}

const EMPTY: Partial<Employee> = {
  full_name: '', id_number: '', tax_number: '', email: '', phone: '', job_title: '',
  pay_type: 'monthly', base_salary: 0, uif_applicable: true, sdl_applicable: true,
};

export default function Employees() {
  const { currentOrg } = useOrg();
  const [items, setItems] = useState<Employee[]>([]);
  const [editing, setEditing] = useState<Partial<Employee> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    if (!currentOrg) return;
    const { data } = await supabase.from('dodobooks_employees').select('*')
      .eq('org_id', currentOrg.id).order('full_name');
    setItems((data ?? []) as Employee[]);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentOrg]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!currentOrg || !editing) return;
    setBusy(true); setErr(null);
    const payload = { ...editing, org_id: currentOrg.id, base_salary: Number(editing.base_salary) || 0 };
    if (editing.id) {
      const { error } = await supabase.from('dodobooks_employees').update(payload).eq('id', editing.id);
      if (error) { setErr(error.message); setBusy(false); return; }
    } else {
      const { error } = await supabase.from('dodobooks_employees').insert(payload);
      if (error) { setErr(error.message); setBusy(false); return; }
    }
    setBusy(false); setEditing(null); load();
  }

  async function archive(id: string) {
    if (!confirm('Archive this employee? They stop appearing in new payroll runs.')) return;
    await supabase.from('dodobooks_employees').update({ is_archived: true }).eq('id', id);
    load();
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="h-page flex items-center gap-2"><Users size={22} /> Employees</h1>
          <p className="text-sm text-text-mute mt-1">SA-specific payroll register. Drives EMP201, EMP501, payslips and journal postings.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing({ ...EMPTY })}><Plus size={14} /> New employee</button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-text-mute uppercase tracking-widest font-mono">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Pay</th>
              <th className="px-3 py-2">UIF</th>
              <th className="px-3 py-2">SDL</th>
              <th className="px-3 py-2">Hired</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.filter((e) => !e.is_archived).map((e) => (
              <tr key={e.id} className="border-t border-line hover:bg-ink-2/50 cursor-pointer" onClick={() => setEditing(e)}>
                <td className="px-3 py-2 font-semibold">{e.full_name}<div className="text-[10px] text-text-mute">{e.email}</div></td>
                <td className="px-3 py-2">{e.job_title ?? '—'}</td>
                <td className="px-3 py-2 font-mono">{formatMoney(e.base_salary, 'ZAR')}/{e.pay_type === 'hourly' ? 'hr' : 'mo'}</td>
                <td className="px-3 py-2">{e.uif_applicable ? '✓' : '—'}</td>
                <td className="px-3 py-2">{e.sdl_applicable ? '✓' : '—'}</td>
                <td className="px-3 py-2 text-xs">{e.hire_date ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={(ev) => { ev.stopPropagation(); archive(e.id); }} className="text-text-mute hover:text-rust"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.filter((e) => !e.is_archived).length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-text-mute text-sm">No employees yet. Click <strong>New employee</strong> to start your register.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <form onSubmit={save} className="card w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="h-section text-base">{editing.id ? 'Edit employee' : 'New employee'}</h2>
              <button type="button" className="text-text-mute hover:text-text" onClick={() => setEditing(null)}><X size={16} /></button>
            </div>

            <div className="field"><label>Full name *</label><input required value={editing.full_name ?? ''} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="field"><label>SA ID number</label><input value={editing.id_number ?? ''} onChange={(e) => setEditing({ ...editing, id_number: e.target.value })} maxLength={13} placeholder="13 digits" /></div>
              <div className="field"><label>Tax number</label><input value={editing.tax_number ?? ''} onChange={(e) => setEditing({ ...editing, tax_number: e.target.value })} /></div>
              <div className="field"><label>Email</label><input type="email" value={editing.email ?? ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
              <div className="field"><label>Phone</label><input value={editing.phone ?? ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div className="field"><label>Job title</label><input value={editing.job_title ?? ''} onChange={(e) => setEditing({ ...editing, job_title: e.target.value })} /></div>
              <div className="field"><label>Hire date</label><input type="date" value={editing.hire_date ?? ''} onChange={(e) => setEditing({ ...editing, hire_date: e.target.value })} /></div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="field"><label>Pay type</label>
                <select value={editing.pay_type ?? 'monthly'} onChange={(e) => setEditing({ ...editing, pay_type: e.target.value as any })}>
                  <option value="monthly">Monthly salary</option>
                  <option value="hourly">Hourly rate</option>
                </select>
              </div>
              <div className="field col-span-2"><label>Gross {editing.pay_type === 'hourly' ? '/hour' : '/month'} (R)</label>
                <input type="number" step="0.01" value={editing.base_salary ?? 0} onChange={(e) => setEditing({ ...editing, base_salary: Number(e.target.value) })} />
              </div>
            </div>

            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-emerald" checked={!!editing.uif_applicable} onChange={(e) => setEditing({ ...editing, uif_applicable: e.target.checked })} />UIF applicable</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-emerald" checked={!!editing.sdl_applicable} onChange={(e) => setEditing({ ...editing, sdl_applicable: e.target.checked })} />SDL applicable</label>
            </div>

            <details className="mb-3">
              <summary className="cursor-pointer text-sm text-text-mute">Bank details (for net pay)</summary>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="field"><label>Bank</label><input value={editing.bank_name ?? ''} onChange={(e) => setEditing({ ...editing, bank_name: e.target.value })} /></div>
                <div className="field"><label>Account</label><input value={editing.bank_account_number ?? ''} onChange={(e) => setEditing({ ...editing, bank_account_number: e.target.value })} /></div>
                <div className="field"><label>Branch</label><input value={editing.bank_branch_code ?? ''} onChange={(e) => setEditing({ ...editing, bank_branch_code: e.target.value })} /></div>
              </div>
            </details>

            {err && <div className="text-rust text-sm mb-2">{err}</div>}
            <div className="flex gap-2">
              <button type="button" className="btn btn-ghost flex-1" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary flex-1" disabled={busy}><Save size={14} /> {busy ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
